/**
 * Fee Account Table Component
 * Displays transaction history grouped by Fee Account Name with rowspan grouping
 */
import React, { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import moment from 'moment';

export class FeeAccountTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            transactions: [],
            flattenedData: [],
            isLoading: false
        };

        // Create refs for scroll synchronization
        this.dataScrollRef = React.createRef();
        this.grandTotalScrollRef = React.createRef();

        // Bind methods
        this.syncScroll = this.syncScroll.bind(this);
        this.processTransactionsData = this.processTransactionsData.bind(this);
    }

    componentDidMount() {
        // Check if transactionsData is already passed as prop
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData) {
            // Use data from props
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false) {
            // Data already loaded but empty
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }
    }

    processTransactionsData(data) {
        console.log('FeeAccountTable - Processing transactions data:', data);
        
        let collections = data?.collections || [];
        if (collections && collections.length > 0) {
            const transformedData = this.transformApiDataToTableFormat(collections);
            console.log('FeeAccountTable - Transformed Data:', transformedData);
            
            const flattened = this.flattenTransactions(transformedData);
            console.log('FeeAccountTable - Flattened Data:', flattened);
            
            this.setState({
                transactions: transformedData,
                flattenedData: flattened,
                isLoading: false
            }, () => {
                // Attach scroll listener after data loads
                setTimeout(() => {
                    this.attachScrollListener();
                }, 100);
            });
        } else {
            console.log('FeeAccountTable - No collections in data');
            this.setState({ 
                transactions: [],
                flattenedData: [],
                isLoading: false 
            });
        }
    }

    componentDidUpdate(prevProps) {
        // Check if transactionsData prop has changed
        const { transactionsData, transactionsLoading } = this.props;
        
        if (transactionsData !== prevProps.transactionsData && transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false && prevProps.transactionsLoading === true && !transactionsData) {
            // Loading finished but no data
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }
    }

    componentWillUnmount() {
        // Clean up scroll event listener
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper) {
            grandTotalWrapper.removeEventListener('scroll', this.syncScroll);
        }
    }

    attachScrollListener = () => {
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper) {
            // Remove existing listener first to avoid duplicates
            grandTotalWrapper.removeEventListener('scroll', this.syncScroll);
            // Add new listener
            grandTotalWrapper.addEventListener('scroll', this.syncScroll);
            console.log('Scroll listener attached to grand total');
        }
    }

    syncScroll(e) {
        const scrollLeft = e.target.scrollLeft;
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (dataWrapper) {
            dataWrapper.scrollLeft = scrollLeft;
            console.log('Syncing scroll - scrollLeft:', scrollLeft);
        }
    }

    flattenTransactions = (feeAccounts) => {
        const flattened = [];
        let globalIndex = 0;
        
        feeAccounts.forEach((account, accountIndex) => {
            // Group fee details by receipt number (unique identifier for each transaction)
            const transactionGroups = {};
            account.feeDetails.forEach(feeDetail => {
                const receiptNo = feeDetail.receiptNo;
                if (!transactionGroups[receiptNo]) {
                    transactionGroups[receiptNo] = [];
                }
                transactionGroups[receiptNo].push(feeDetail);
            });

            // Flatten each transaction group
            Object.keys(transactionGroups).forEach(receiptNo => {
                const txnFeeDetails = transactionGroups[receiptNo];
                const totalFeeDetails = txnFeeDetails.length;
                
                txnFeeDetails.forEach((feeDetail, feeIndex) => {
                    flattened.push({
                        sNo: account.sNo,
                        feeAccountName: account.feeAccountName,
                        feeDetail: feeDetail,
                        accountIndex: accountIndex,
                        globalIndex: globalIndex,
                        feeIndex: feeIndex,
                        isFirstRowInAccount: globalIndex === 0,
                        isFirstRowInTransaction: feeIndex === 0,
                        isLastRowInTransaction: feeIndex === totalFeeDetails - 1,
                        transactionRowSpan: totalFeeDetails,
                        transactionId: feeDetail.transactionId,
                        receiptNo: receiptNo,
                        uniqueKey: `${account.sNo}-${receiptNo}-${feeIndex}`
                    });
                    globalIndex++;
                });
            });
        });
        
        return flattened;
    };

    /**
     * Transform API response to table format
     * API response has collections array, each with transctionId, receiptNo, branchName, feePaidDetails array
     * Transform to: [{ sNo, feeAccountName, feeDetails: [...] }]
     */
    transformApiDataToTableFormat = (collections) => {
        // Since all collections are from same branch, we can group them all under one account
        if (!collections || collections.length === 0) {
            return [];
        }

        // Get branch name from first collection (all should have same branch)
        const branchName = collections[0].branchName || "Unknown Branch";

        // Transform all collections into feeDetails array
        const allFeeDetails = [];

        collections.forEach((collection) => {
            const transactionId = collection.transctionId || '';
            const receiptNo = collection.receiptNo || 'N/A';
            const receiptDate = collection.receiptDate || null;
            const transactionDate = collection.transactionDate || null;
            const remarks = collection.remarks || null;
            const totalAmountPaid = collection.amountPaid || 0;

            // Each feePaidDetails item becomes a row in the table
            if (collection.feePaidDetails && collection.feePaidDetails.length > 0) {
                collection.feePaidDetails.forEach((feeDetail) => {
                    allFeeDetails.push({
                        transactionId: transactionId,
                        remarks: remarks,
                        receiptNo: receiptNo,
                        receiptDate: receiptDate,
                        transactionDate: transactionDate,
                        feeType: `${feeDetail.feeType} - ${feeDetail.paymentTerm}`,
                        paidAmount: feeDetail.amountPaying || 0,
                        total: totalAmountPaid
                    });
                });
            }
        });

        // Return single account with all fee details
        return [
            {
                sNo: 1,
                feeAccountName: branchName,
                feeDetails: allFeeDetails
            }
        ];
    };

    formatDate = (dateString) => {
        if (!dateString) return '';
        const date = moment(dateString);
        return date.isValid() ? date.format('DD/MM/YYYY') : dateString;
    };

    calculateGrandTotal = () => {
        const { flattenedData } = this.state;
        return flattenedData.reduce((sum, row) => sum + (row.feeDetail.paidAmount || 0), 0);
    };

    // Column body templates
    snoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInAccount) return '';
        return (
            <span style={{ display: 'block', textAlign: 'center' }}>
                {rowData.sNo}
            </span>
        );
    };

    feeAccountNameBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInAccount) return '';
        return <span>{rowData.feeAccountName}</span>;
    };

    transactionIdBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInTransaction) return '';
        const txnId = rowData.feeDetail.transactionId || 'N/A';
        return (
            <div className="transaction-id-info">
                <div className="transaction-id">{txnId}</div>
                {rowData.feeDetail.remarks && (
                    <div className="remarks">Remarks : {rowData.feeDetail.remarks}</div>
                )}
            </div>
        );
    };

    receiptNoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInTransaction) return '';
        return <span>{rowData.feeDetail.receiptNo}</span>;
    };

    autoReceiptNoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInTransaction) return '';
        return <span>{rowData.feeDetail.autoReceiptNo}</span>;
    };

    receiptDateBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInTransaction) return '';
        return <span>{this.formatDate(rowData.feeDetail.receiptDate)}</span>;
    };

    feeTypeBodyTemplate = (rowData) => {
        return <span>{rowData.feeDetail.feeType}</span>;
    };

    paidAmountBodyTemplate = (rowData) => {
        return <span>{rowData.feeDetail.paidAmount.toFixed(2)}</span>;
    };

    totalBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInTransaction) return '';
        return <span>{rowData.feeDetail.total.toFixed(2)}</span>;
    };

    rowClassName = (rowData) => {
        const classes = [];
        
        // Add class for first row to exclude it from getting top border
        if (rowData.isFirstRowInAccount) {
            classes.push('fee-account-first-row');
        }
        
        // Add class for first row in transaction to add top border
        if (rowData.isFirstRowInTransaction) {
            classes.push('transaction-start-row');
        }
        
        // Add class for last row in transaction to add thick bottom border
        if (rowData.isLastRowInTransaction) {
            classes.push('fee-account-group-last-row');
        }
        
        return classes.join(' ');
    };

    render() {
        const { flattenedData, isLoading } = this.state;
        const grandTotal = this.calculateGrandTotal();

        return (
            <div className="fee-account-table-container">
                 <div className="info-note">
                    <i className="pi pi-info-circle"></i>
                    <span>Note : Transactions which are showing not including of Other fee Collections, School Store Transactions.</span>
                </div>

                {isLoading ? (
                    <div className="loading-container" style={{ textAlign: 'center', padding: '2rem' }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <p>Loading transactions...</p>
                    </div>
                ) : (
                <div className="fee-account-table-wrapper">
                    <div ref={this.dataScrollRef} className="data-table-scroll-container">
                        <DataTable
                            value={flattenedData}
                            scrollable
                            scrollHeight="flex"
                            className="fee-account-table"
                            showGridlines
                            rowClassName={this.rowClassName}
                            emptyMessage="No results found"
                        >
                            <Column
                                header="S.No"
                                body={this.snoBodyTemplate}
                                headerClassName="sno-header"
                                bodyClassName="sno-cell"
                                style={{ width: '70px', minWidth: '70px' }}
                            />
                            <Column
                                header="Fee Account Name"
                                body={this.feeAccountNameBodyTemplate}
                                headerClassName="fee-account-name-header"
                                bodyClassName="fee-account-name-cell"
                                style={{ width: '180px', minWidth: '180px' }}
                            />
                            <Column
                                header="TransactionID"
                                body={this.transactionIdBodyTemplate}
                                headerClassName="transaction-id-header"
                                bodyClassName="transaction-id-cell"
                                style={{ width: '250px', minWidth: '250px' }}
                            />
                            <Column
                                header="Receipt No"
                                body={this.receiptNoBodyTemplate}
                                headerClassName="receipt-no-header"
                                bodyClassName="receipt-no-cell"
                                style={{ width: '130px', minWidth: '130px' }}
                            />
                            <Column
                                header="Receipt Date"
                                body={this.receiptDateBodyTemplate}
                                headerClassName="receipt-date-header"
                                bodyClassName="receipt-date-cell"
                                style={{ width: '130px', minWidth: '130px' }}
                            />
                            <Column
                                header="Fee Type"
                                body={this.feeTypeBodyTemplate}
                                headerClassName="fee-type-header"
                                bodyClassName="fee-type-cell"
                                style={{ width: '250px', minWidth: '250px' }}
                            />
                            <Column
                                header="Paid Amount"
                                body={this.paidAmountBodyTemplate}
                                headerClassName="paid-amount-header"
                                bodyClassName="paid-amount-cell"
                                style={{ width: '130px', minWidth: '130px' }}
                            />
                            <Column
                                header="Total"
                                body={this.totalBodyTemplate}
                                headerClassName="total-header"
                                bodyClassName="total-cell"
                                style={{ width: '130px', minWidth: '130px' }}
                            />
                        </DataTable>
                    </div>

                    {/* Grand Total Row */}
                    <div ref={this.grandTotalScrollRef}>
                        <DataTable
                            value={[{ total: grandTotal }]}
                            className="fee-account-grand-total-table"
                            scrollable
                            scrollHeight="flex"
                            showGridlines
                        >
                            <Column body={() => ''} style={{ width: '70px', minWidth: '70px' }} />
                            <Column body={() => ''} style={{ width: '180px', minWidth: '180px' }} />
                            <Column body={() => ''} style={{ width: '250px', minWidth: '250px' }} />
                            <Column body={() => ''} style={{ width: '130px', minWidth: '130px' }} />
                            <Column body={() => ''} style={{ width: '130px', minWidth: '130px' }} />
                            <Column body={() => <strong style={{ color: '#076EFF' }}>GRAND TOTAL :</strong>} style={{ width: '250px', minWidth: '250px', textAlign: 'left' }} />
                            <Column body={() => <strong style={{ color: '#076EFF' }}>₹{grandTotal.toFixed(2)}</strong>} style={{ width: '130px', minWidth: '130px', textAlign: 'right' }} />
                            <Column body={() => ''} style={{ width: '130px', minWidth: '130px' }} />
                        </DataTable>
                    </div>
                </div>
                )}
            </div>
        );
    }
}
